import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ListData } from './list-data.service';
import { ItemsListContentComponent } from './list-content/list-content.component';
import { ObjectTypesService } from '@fi-app/utilities/fi-services/object-types.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { ErrorMessageService } from '../../utilities/fi-services/error-message.service';
import { UserPreferencesService } from '@fi-shared/fi-shared-services/user-preferences.service';
import { SideListOptionType } from '@fi-shared/types/side-list-option-type';
import { FiTagsListPositionTypes } from './enums';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { isFunction } from '@fi-shared/utils/is-function';
import { AppTextService } from '@fi-shared/app-text';
import { FiStateChangeService } from '@fi-shared/fi-state-proxy/fi-state-change.service';
import { takeUntil } from 'rxjs/operators';
import { FiItemsListSettings } from '@fi-app/components/items-list/items-list-injectors';
import { IBackendErrorData } from '@fi-shared/fi-backend/fi-backend.service';
import { DetectDevicesService, FiSnackBarService } from '@fi-shared/fi-shared-services';
import { fiDebounce, getObjectTypeNameTextId, isDefined } from '@fi-shared/utils';
import { ObjectTypes } from '@fi-shared/types';
import { FiItemsListHelperService } from '@fi-shared/fi-items-list/fi-items-list-helper.service';
import { FiDisplayType } from '@fi-shared/types/display-type';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiLocalSearch2Component } from '@fi-shared/fi-local-search2/fi-local-search2.component';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';

@Component({
    selector: 'fi-items-list',
    templateUrl: './items-list.component.html',
    styleUrls: ['./items-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: FiItemsListSettings,
            useFactory: (instance: ItemsListComponent) => instance.ngModel,
            deps: [ItemsListComponent],
        },
    ],
    host: { class: 'fi-items-list' },
    standalone: false,
})
export class ItemsListComponent implements OnInit, OnDestroy {
    @Input('settings') ngModel: ListData<any>;
    @Input() module = [];
    @Input() moduleExactMatch = false;
    @Input() isOverflowScroll = false;
    @Input() reportType = 0;
    @Input() isWideSize = false;
    @Input() doHighlight = false;
    @ViewChild('itemsListContent', { static: true }) itemsListContent: ItemsListContentComponent;
    @ViewChild('topTags', { static: false }) topTagsElementRef: ElementRef;
    @ViewChild('topTagsContent', { static: false }) topTagsContentElementRef: ElementRef;
    @ViewChild('contentItems', { static: false }) contentItemsElementRef: ElementRef;
    @ViewChild('contentListContentWrapper', { static: true }) contentListContentWrapperElementRef: ElementRef;
    @ViewChild(FiLocalSearch2Component) localSearchComponent: FiLocalSearch2Component;

    IsToggleAll: boolean = false;
    @Input() sideListOptionsType;
    MoreActionsOpened: boolean = false;
    IsAllTagsShowing: boolean = false;
    DisplayTags: boolean = false;
    allTagsToggled = false;
    CheckingShowMore: boolean = false;

    unSubscribeToScrollFn: () => void;

    constructor(
        private renderer: Renderer2,
        private fiDialogsService: FiDialogsService,
        private objectTypesService: ObjectTypesService,
        private fiLoadingService: LoadingService,
        private errorMessageService: ErrorMessageService,
        private userPreferencesService: UserPreferencesService,
        private stateService: FiStateService,
        private appTextService: AppTextService,
        private fiStateChangeService: FiStateChangeService,
        private detectDevices: DetectDevicesService,
        private snackBar: FiSnackBarService,
        private itemsListHelperService: FiItemsListHelperService,
        private userDataService: FiUserDataService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        this.DisplayTags =
            this.ngModel.ListViewSettings && this.ngModel.ListViewSettings.PositionType === FiTagsListPositionTypes.Top;

        this.ngModel.displayTypeChanged$.pipe(takeUntil(this.ngModel.ngOnDestroy$)).subscribe(() => {
            if (this.unSubscribeToScrollFn) this.unSubscribeToScrollFn();

            this.unSubscribeToScrollFn = this.subscribeToScroll();

            this.userPreferencesService.saveListDisplayType(this.ngModel.ObjectType, this.ngModel.DisplayType);
            this.cdr.detectChanges();
        });

        this.ngModel.displayTypeLoaded$.pipe(takeUntil(this.ngModel.ngOnDestroy$)).subscribe(() => {
            this.ngModel.DisplayType =
                this.userPreferencesService.getListDisplayType(this.ngModel.ObjectType) || FiDisplayType.List;
        });

        this.ngModel.selectedItems$.pipe(takeUntil(this.ngModel.ngOnDestroy$)).subscribe(() => {
            this.cdr.markForCheck();
        });

        this.ngModel.favoriteChanged$.pipe(takeUntil(this.ngModel.ngOnDestroy$)).subscribe(() => {
            this.cdr.markForCheck();
        });

        this.ngModel.dataCleared$.pipe(takeUntil(this.ngModel.ngOnDestroy$)).subscribe(() => {
            this.IsToggleAll = false;
        });

        this.ngModel.onAppendData.pipe(takeUntil(this.ngModel.ngOnDestroy$)).subscribe((loadedDataCount) => {
            if (this.IsToggleAll) this.ToggleAll();

            setTimeout(() => {
                if (loadedDataCount !== 0 && this.IsGridView()) this.checkShowMore();
            });
        });

        // called from FiItemsListMoreMenuMobileComponent or from SystemNotificationsService
        this.ngModel.onDeleteClick.pipe(takeUntil(this.ngModel.ngOnDestroy$)).subscribe((isPermanently) => {
            this._doDelete(isPermanently);
        });

        this.unSubscribeToScrollFn = this.subscribeToScroll();

        this.ngModel.onInitialized.pipe(takeUntil(this.ngModel.ngOnDestroy$)).subscribe((offset) => {
            const wrapper: HTMLElement = this.contentListContentWrapperElementRef.nativeElement;
            if (!offset) return;
            setTimeout(() => {
                // scroll to offset on loading
                const elem = wrapper.querySelectorAll('div.CcmList').item(offset) as HTMLElement;
                if (elem) {
                    wrapper.scrollTop = elem.offsetTop - wrapper.offsetTop;
                }
            });
        });

        // FI-1745 Viewing a page sometimes doesn't work
        // remove scroll listener on state change because debounce update state after it has been already changed
        // this issue doesn't allow user to go to another state until last debounced onScroll fired
        const currentStateName = this.stateService.current.name;
        this.fiStateChangeService.onStart$.pipe(takeUntil(this.ngModel.ngOnDestroy$)).subscribe((tr) => {
            if (currentStateName !== tr.from().name && this.unSubscribeToScrollFn) {
                this.unSubscribeToScrollFn();
                this.unSubscribeToScrollFn = undefined;
            }
        });

        this.ngModel.LoadingMore$.pipe(takeUntil(this.ngModel.ngOnDestroy$)).subscribe((isLoading) => {
            if (!isLoading) {
                this.checkShowMore();
            }
        });

        this.subscribeOnPreferredBusinessUnitChange();
    }

    private subscribeOnPreferredBusinessUnitChange() {
        this.userDataService.onPreferredBusinessUnitChanged$
            .pipe(takeUntil(this.ngModel.ngOnDestroy$))
            .subscribe(() => {
                this.ngModel.clearData();
                setTimeout(() => {
                    this.ngModel.Initialize();
                });
            });
    }

    private subscribeToScroll() {
        const wrapper: HTMLElement = this.contentListContentWrapperElementRef.nativeElement;
        const debouncedScroll = fiDebounce(() => {
            // FI-1745 Viewing a page sometimes doesn't work
            // if unSubscribeToScrollFn already removed - do nothing. state changed
            if (this.unSubscribeToScrollFn) {
                // https://chainformation.atlassian.net/browse/FI-1013
                const isDropDownMenuOpen = !!wrapper.querySelectorAll('.open>.dropdown-menu').length;
                if (isDropDownMenuOpen) return;

                const wrapperTop = wrapper.offsetTop;
                const elementIndex = Array.from(wrapper.querySelectorAll('div.CcmList')).findIndex(
                    (element: HTMLElement) => {
                        return element.getBoundingClientRect().top + 6 >= wrapperTop;
                    },
                );
                if (isDefined(elementIndex)) {
                    this.ngModel.onScroll(elementIndex);
                }
            }
        }, 200);
        wrapper.addEventListener('scroll', debouncedScroll);
        return function () {
            wrapper.removeEventListener('scroll', debouncedScroll);
        };
    }

    isShowToolbar() {
        return (
            (this.ngModel.ShowDelete && !this.detectDevices.isMobilePhone()) ||
            (this.ngModel.Buttons.length > 0 && this.ngModel.IsExtraButtonsShown) ||
            this.ngModel.SearchFunction
        );
    }

    isWithChildren() {
        if (isFunction(this.ngModel.isWithChildren)) return this.ngModel.isWithChildren();

        return !!this.ngModel.ChildProperty;
    }

    IsShowToggleAllCheckbox() {
        return !!this.ngModel.MultiSelect && this.IsShowCheckbox();
    }

    IsShowCheckbox(dataItem?) {
        if (isFunction(this.ngModel.ShowCheckBox)) return this.ngModel.ShowCheckBox(dataItem);

        return this.ngModel.ShowCheckBox;
    }

    // note: not used ?
    IsCheckboxDisabled(data) {
        return this.ngModel.IsCheckboxDisabled(data);
    }

    IsShowFavorite() {
        return this.ngModel.ShowFavoriteIcon;
    }

    // note: not used ?
    GetGridHeader(Header) {
        return [Header[0], Header[this.ngModel.NameColumnIndex], Header[this.ngModel.GridSecondColumnIndex]];
    }

    IsListView() {
        return this.ngModel.DisplayType == FiDisplayType.List;
    }

    IsGridView() {
        return this.ngModel.DisplayType == FiDisplayType.Grid;
    }

    IsShowDeleteButton() {
        return this.ngModel.ShowDelete && this.sideListOptionsType != SideListOptionType.Deleted;
    }

    IsSomethingChecked() {
        return this.ngModel.SelectedItems.length > 0;
    }

    isDeleteDisabled() {
        return !this.IsSomethingChecked() || this.ngModel.isDeleteDisabled();
    }

    IsShowMoreButton() {
        return this.ngModel.ShowDelete && this.sideListOptionsType == SideListOptionType.Deleted;
    }

    moreActionsHide;

    ToggleMoreActions($event) {
        $event.stopPropagation();
        setTimeout(() => {
            this.MoreActionsOpened = !this.MoreActionsOpened;
            if (this.MoreActionsOpened) {
                this.moreActionsHide = this.renderer.listen('document', 'click', (event) => {
                    this.MoreActionsOpened = false;
                    // if (!this.elementRef.nativeElement.contains(event.target)) {
                    //     this.closeDropdown();
                    // }
                });
                // w.on('click', moreActionsHideFunction);
            }
        });
    }

    // note: not used ?
    ToggleExpandData() {}

    Delete() {
        this._doDelete();
    }

    Restore(itemsToRestore?: any[]): Promise<void> {
        if (!itemsToRestore) itemsToRestore = this.ngModel.SelectedItems;

        const promise = this.ngModel.restoreFunc(itemsToRestore);
        promise.then(
            () => this.afterItemsRestored(itemsToRestore),
            (response) => this.afterItemsRestored(itemsToRestore, response),
        );

        return promise;
    }

    DeletePermanently() {
        this._doDelete(true);
    }

    ToggleAll() {
        this.ngModel.SelectedItems = this.IsToggleAll
            ? this.ngModel.getItemsWithChildrenRecursively(this.ngModel.Data)
            : [];
        //this.itemsListContent.processRowsChecked();
        this.ngModel.raiseSelectedItemsChanged();
    }

    isIndeterminateCheckAllCheckboxStatus(): boolean {
        return (
            !!this.ngModel.SelectedItems?.length &&
            this.ngModel.SelectedItems.length !== this.ngModel.getItemsWithChildrenRecursively(this.ngModel.Data).length
        );
    }

    showAllTagsToggle() {
        this.allTagsToggled = true;
        this.IsAllTagsShowing = !this.IsAllTagsShowing;

        if (this.IsAllTagsShowing) {
            this.renderer.setStyle(
                this.topTagsElementRef.nativeElement,
                'height',
                this.topTagsContentElementRef.nativeElement.getBoundingClientRect().height + 'px',
            );
            return;
        }
        this.renderer.removeStyle(this.topTagsElementRef.nativeElement, 'height');
    }

    isShowToggleTags() {
        return (
            this.allTagsToggled ||
            (this.topTagsElementRef &&
                this.topTagsContentElementRef &&
                this.topTagsElementRef.nativeElement.getBoundingClientRect().height <
                    this.topTagsContentElementRef.nativeElement.getBoundingClientRect().height)
        );
        // ||
        //     element.find('.CcmListTopTags').height() < element.find('.CcmListTopTagsContent').height();
    }

    //
    // var digestOnResize = function () {
    //     scope.$digest();
    // };
    //
    // w.on('resize', digestOnResize);
    //
    // scope.$on('$destroy', function () {
    //     w.off('resize', digestOnResize);
    // });
    //
    //
    checkShowMore() {
        this.CheckingShowMore = true;
        setTimeout(() => {
            const contentListContentWrapperElement: HTMLElement =
                this.contentListContentWrapperElementRef.nativeElement;
            if (
                contentListContentWrapperElement.offsetHeight + contentListContentWrapperElement.scrollTop >=
                contentListContentWrapperElement.scrollHeight
            ) {
                this.ngModel.ShowMorePromise().then(
                    () => {
                        //this.checkShowMore();
                    },
                    () => {
                        this.CheckingShowMore = false;
                    },
                );
                return;
            }
            this.CheckingShowMore = false;
        });
    }

    GetClass(properties, row?) {
        let _class = '';
        if (properties.Class) _class += isFunction(properties.Class) ? properties.Class(row) : properties.Class;

        if (properties && properties.ExtraProperties && isFunction(properties.ExtraProperties.GetClass))
            _class += ' ' + properties.ExtraProperties.GetClass(row);

        return _class;
    }

    private triggerSearchDebounced = fiDebounce(() => this.ngModel.SearchFunction(), 100);
    localSearchChange() {
        // todo by some reason method triggers twice on enter click in LocalSearchComponent
        this.triggerSearchDebounced();
        //this.ngModel.SearchFunction();
    }
    onLocalSearchTextChanged($event: string) {
        this.ngModel.raiseLocalSearchTextChanged($event);
    }

    // todo: remove, not used
    afterUpdateContent() {
        this.checkShowMore();
    }

    ngOnDestroy() {
        this.ngModel.ngOnDestroy$.next();
        this.ngModel.ngOnDestroy$.complete();
    }

    deleteConfirm(deleteConfirmMessage: string, selectedIds?: number[], selectedItems?: any[]): Promise<any[] | void> {
        if (this.ngModel.deleteConfirm) {
            const confirmResult = this.ngModel.deleteConfirm(deleteConfirmMessage, selectedIds, selectedItems);
            if (confirmResult) return confirmResult;
        }

        return new Promise<void>((resolve, reject) => {
            this.fiDialogsService
                .confirm({
                    messageText: deleteConfirmMessage,
                    headerText: this.appTextService.get('common.warning'),
                    yesButtonText: this.appTextService.get('common.delete'),
                })
                .onYesButtonClick(resolve)
                .onNoButtonClick(reject);
        });
    }

    _doDelete(isPermanently = false): any {
        if (this.ngModel.doDelete() === false) return;

        const promise = new Promise<void>((resolve, reject) => {
            let selectedItems = this.ngModel.GetCheckedItems();
            const isPlural = selectedItems.length > 1;

            const itemName = isFunction(this.ngModel.GetItemName)
                ? this.ngModel.GetItemName(isPlural).toLowerCase()
                : this.objectTypesService.GetNameByObjectType(this.ngModel.ObjectType, isPlural).toLowerCase();
            const selectedText = this.itemsListHelperService.getSelectedTextByObjectType(
                this.ngModel.ObjectType,
                isPlural,
            );

            const deleteConfirmText =
                isPlural && this.ngModel.DeleteMultipleConfirmMessage
                    ? this.ngModel.DeleteMultipleConfirmMessage
                    : this.ngModel.DeleteConfirmMessage;

            const deleteConfirmMessage = String.format(
                !isPermanently ? deleteConfirmText : this.appTextService.get(this.ngModel.DeletePermanentlyTextId),
                itemName,
                selectedText,
            );

            let selectedIds: number[] = selectedItems.map((item) => item[this.ngModel.IdColumn]);

            this.deleteConfirm(deleteConfirmMessage, selectedIds, selectedItems).then((confirmResult) => {
                if (Array.isArray(confirmResult)) {
                    selectedItems = <any>confirmResult;
                    selectedIds = selectedItems.map((item) => item[this.ngModel.IdColumn]);
                }

                const promise = this.ngModel.deleteFunc(selectedIds, selectedItems);

                if (!promise) {
                    reject();
                    return;
                }

                this.fiLoadingService.trackPromise(promise);

                const deletedItems = selectedItems.slice();
                promise.then(
                    (failures) => {
                        if (!failures || failures === true) {
                            failures = [];
                        } else if (failures === false) {
                            failures = selectedIds;
                        }
                        this.ngModel.RemoveFromList(selectedItems, failures);
                        this.showDeleteUndoSnackbar(deletedItems);
                        resolve();
                    },
                    (response: IBackendErrorData) => {
                        if (
                            response.error &&
                            response.error.Data &&
                            response.error.Data.ObjectId &&
                            response.error.Data.ObjectId.length
                        ) {
                            this.ngModel.RemoveFromList(selectedItems, response.error.Data.ObjectId);
                            this.showDeleteUndoSnackbar(deletedItems, response);
                        }
                        reject();
                    },
                );
            });
        });

        return promise;
    }

    private showDeleteRestoreSnackbar() {
        return (
            this.ngModel.Statefull &&
            (this.ngModel.ObjectType === ObjectTypes.Document ||
                this.ngModel.ObjectType === ObjectTypes.Template ||
                this.ngModel.ObjectType === ObjectTypes.MediaBank ||
                this.ngModel.ObjectType === ObjectTypes.CheckListTemplate)
        );
    }
    private showDeleteUndoSnackbar(itemsToDelete: any[], errorResponse?: IBackendErrorData) {
        if (this.showDeleteRestoreSnackbar()) {
            const failedIds =
                errorResponse && errorResponse.error && errorResponse.error.Data && errorResponse.error.Data.ObjectId
                    ? errorResponse.error.Data.ObjectId
                    : [];

            const isRestoreEnabled = this.ngModel.FilterType !== SideListOptionType.Deleted;
            const deletedItems = itemsToDelete.filter((item) => failedIds.indexOf(item[this.ngModel.IdColumn]) === -1);

            const snackbarTextMessage =
                deletedItems.length +
                ' ' +
                this.appTextService.get(getObjectTypeNameTextId(this.ngModel.ObjectType, deletedItems.length !== 1)) +
                ' ' +
                this.appTextService.get(isRestoreEnabled ? 'history.moved_to_trash' : 'history.deleted').toLowerCase();
            const snackbarButtonText = this.appTextService.get(isRestoreEnabled ? 'common.undo' : 'common.dismiss');

            this.snackBar
                .showSnackbar({
                    textMessage: snackbarTextMessage,
                    buttonText: snackbarButtonText,
                })
                .onAction()
                .subscribe(() => {
                    if (isRestoreEnabled) this.Restore(deletedItems);
                });
        }
    }

    private afterItemsRestored(itemsToRestore: any[], errorResponse?: IBackendErrorData) {
        const failedIds =
            errorResponse && errorResponse.error && errorResponse.error.Data && errorResponse.error.Data.ObjectId
                ? errorResponse.error.Data.ObjectId
                : [];

        const restoredItems = itemsToRestore.filter((item) => failedIds.indexOf(item[this.ngModel.IdColumn]) === -1);

        const textMessage =
            restoredItems.length +
            ' ' +
            this.appTextService.get(getObjectTypeNameTextId(this.ngModel.ObjectType, restoredItems.length !== 1)) +
            ' ' +
            this.appTextService.get('history.restored').toLowerCase();

        if (this.showDeleteRestoreSnackbar()) {
            this.snackBar.showSnackbar({
                textMessage: textMessage,
                buttonText: this.appTextService.get('common.dismiss'),
            });
        }

        if (this.ngModel.FilterType === SideListOptionType.Deleted) {
            this.ngModel.RemoveFromList(restoredItems);
        } else {
            this.ngModel.appendData(restoredItems, true);
            this.ngModel.TotalDataCount += restoredItems.length;
        }
        this.cdr.detectChanges();
    }

    ToggleMobileMore() {
        this.ngModel.ToggleMobileMore();
    }

    isWithSearch(): boolean {
        return this.ngModel.isV2SearchEnabled && !this.localSearchComponent.isEmpty();
    }
}
